import { useState, type ReactNode } from 'react';
import { Square } from 'lucide-react';
import { isCancellable } from '@cp/shared';
import { Button, Drawer, Fact, Facts, StatusPill } from '@/components/ui';
import type { JobDetail } from '@/types/api';
import { formatDateTime } from '@/utils/format';
import { JOB_STATE, JOB_TYPE_LABELS, label, present } from '@/utils/labels';

/** One job, opened from the list: its timeline and the identifiers to report it by. */
export function JobDrawer({
  job,
  onClose,
  onCancel,
}: {
  readonly job: JobDetail | null;
  readonly onClose: () => void;
  readonly onCancel?: (job: JobDetail) => void;
}): ReactNode {
  // Held after `job` goes null so the closing animation has something to draw.
  const [last, setLast] = useState<JobDetail | null>(null);
  if (job && job !== last) setLast(job);

  const data = job ?? last;
  if (!data) return null;

  const state = present(JOB_STATE, data.state);

  return (
    <Drawer
      open={job !== null}
      title={label(JOB_TYPE_LABELS, data.type)}
      description={state.hint}
      onClose={onClose}
      width="sm"
      footer={
        onCancel && isCancellable(data.state) ? (
          <Button variant="danger" icon={Square} onClick={() => onCancel(data)}>
            Отменить задачу
          </Button>
        ) : undefined
      }
    >
      <div className="stack stack-loose">
        <Facts>
          <Fact label="Состояние">
            <StatusPill map={JOB_STATE} status={data.state} />
          </Fact>
          <Fact label="Тип">
            <span className="mono">{data.type}</span>
          </Fact>
          <Fact label="Создана">{formatDateTime(data.createdAt)}</Fact>
          <Fact label="Начата">{data.startedAt ? formatDateTime(data.startedAt) : 'не начиналась'}</Fact>
          <Fact label="Завершена">{data.completedAt ? formatDateTime(data.completedAt) : 'не завершена'}</Fact>
          <Fact label="Попыток">{data.attempts}</Fact>
          {data.errorCode ? (
            <Fact label="Причина отказа">
              <span className="mono tone-down">{data.errorCode}</span>
            </Fact>
          ) : null}
        </Facts>

        {/* The identifiers an operator needs when reporting this job to someone
            else, or grepping for it in the worker log. */}
        <Facts>
          <Fact label="Идентификатор">
            <span className="mono text-sm">{data.id}</span>
          </Fact>
          <Fact label="Ключ идемпотентности">
            <span className="mono text-sm">{data.idempotencyKey}</span>
          </Fact>
          {data.siteId ? (
            <Fact label="Сайт">
              <a className="mono text-sm" href={`/sites/${data.siteId}`}>
                {data.siteId}
              </a>
            </Fact>
          ) : null}
        </Facts>
      </div>
    </Drawer>
  );
}
